export default class Router {
  constructor() {
    this.routes = []
    this.exactRouteByPath = new Map()
    this.loaded = false
    this.pendingInitialDispatch = false
    // Guards a single auto-reload when a deploy swaps chunk hashes mid-session.
    this.chunkReloadKey = 'router:chunk-reload'

    const dispatchInitial = () => {
      if (this.pendingInitialDispatch) return
      this.pendingInitialDispatch = true
      queueMicrotask(() => {
        this.pendingInitialDispatch = false
        this.onDOMContentLoaded()
      })
    }

    this.dispatchInitial = dispatchInitial

    if (document.readyState === 'loading') {
      window.addEventListener('DOMContentLoaded', () => dispatchInitial())
    } else {
      dispatchInitial()
    }
  }

  loadMethod(method) {
    if (typeof method === 'function') {
      let result
      try {
        result = method()
      } catch (error) {
        throw error
      }

      if (result && typeof result.then === 'function') {
        result.then(
          (module) => {
            sessionStorage.removeItem(this.chunkReloadKey)
            if (module && module.default) this.runModuleInit(module.default)
          },
          (error) => this.handleModuleLoadFailure(error)
        )
      }
    } else if (method) {
      throw new Error(`Router unknown handler for ${location.pathname}`)
    } else {
      console.warn(`Router no register for ${location.pathname}`)
    }

    this.loaded = true
  }

  // Errors thrown by a page's own init() are bugs, not stale chunks: reloading
  // can't fix them (and would loop), so log them instead.
  runModuleInit(init) {
    try {
      const result = init()
      if (result && typeof result.catch === 'function') {
        result.catch((error) => console.error('Router: module init failed for', location.pathname, error))
      }
    } catch (error) {
      console.error('Router: module init failed for', location.pathname, error)
    }
  }

  // A stale entry file can reference a hashed chunk removed by a newer deploy.
  // Reload once (not in a loop) to pick up the current entry + chunk set.
  handleModuleLoadFailure(error) {
    console.error('Router: failed to load module for', location.pathname, error)

    if (sessionStorage.getItem(this.chunkReloadKey)) {
      console.error('Router: already reloaded once for this failure, giving up')
      return
    }

    sessionStorage.setItem(this.chunkReloadKey, '1')
    location.reload()
  }

  onDOMContentLoaded() {
    const pathname = location.pathname
    const methods = this.getMatchingMethods(pathname)
    for (const method of methods) {
      this.loadMethod(method)
    }
  }

  on(pathname, method) {
    const routePath = this.normalizePath(pathname)
    const routePattern = this.createExactRoutePattern(routePath)
    const route = {
      type: 'exact',
      path: routePath,
      pattern: this.createUrlPattern(routePattern),
      method,
    }

    const existingIndex = this.exactRouteByPath.get(routePath)
    if (typeof existingIndex === 'number') {
      this.routes[existingIndex] = route
    } else {
      this.routes.push(route)
      this.exactRouteByPath.set(routePath, this.routes.length - 1)
    }

    if (!this.loaded) {
      this.dispatchInitial()
    }
  }

  match(pattern, method) {
    this.routes.push({
      type: 'pattern',
      path: pattern,
      pattern: this.createUrlPattern(pattern),
      method,
    })

    if (!this.loaded) {
      this.dispatchInitial()
    }
  }

  getMatchingMethods(pathname) {
    const methods = []
    for (const route of this.routes) {
      const matches = route.pattern.exec({ pathname })
      if (matches) {
        methods.push(route.method)
      }
    }
    return methods
  }

  normalizePath(pathname) {
    const hasTrailingSlash = pathname.slice(-1) === '/'
    let path
    const length = pathname.length

    if (length === 1) {
      path = pathname
    } else if (hasTrailingSlash) {
      path = pathname.substr(0, length - 1)
    } else {
      path = pathname
    }

    return path
  }

  createExactRoutePattern(pathname) {
    if (pathname === '/') return '/'
    return `${pathname}{/}?`
  }

  createUrlPattern(pathnamePattern) {
    return new URLPattern({ pathname: pathnamePattern })
  }
}
